import { Component, type OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '../../../service/events/dashboard.service';
import { ClientesApiService } from '../../../service/api/clientes-api.service';
import { RequestHandler } from '../../../service/OffService/request-handler';
import { BaseView } from '../../base-view';
import { StorageManager } from '../../../common/storage-manager.class';
import { environment } from '../../../../environments/environment';
import { SdiApiService } from '../../../service/api/sdi.service';
import { createCsv } from '@fitogest/reactive-form-builder/csv';
import { ClienteModel } from '../../../models/cliente.model';
import { ColInterface } from '../../../common/components/common-list-table/col.interface';
import { ColStylesInterface } from '../../../common/components/common-list-table/col-styles.interface';
import { TreeNode } from 'primeng/api';
import { ClientesFormDefinition } from './clientes-form/form-definition';
import { FormType } from '@fitogest/reactive-form-builder/interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Filtering } from '../../../service/filtering/filtering';
import { ClientesEnvironmentDefinition } from './environment-variables';
import { HttpClient } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ClientSelectionService } from '../../../service/client-selection.service';

export interface AdquisicionRow {
  fecha?: string;
  nombre_comercial?: string;
  numero_registro?: string;
  numero_lote?: string;
  clasificacion_toxicologica?: string;
  cantidad?: string;
  suministrador_nropo?: string;
  suministrador_nif?: string;
  suministrador_nombre?: string;
}

export interface AplicacionRow {
  fecha?: string;
  nombre_comercial?: string;
  numero_registro?: string;
  numero_lote?: string;
  capacidad_envase?: string;
  numero_envases?: string;
  sigpac_provincia?: string;
  sigpac_municipio?: string;
  sigpac_parc_rec?: string;
  contratante_razon?: string;
  contratante_sede?: string;
  contratante_nif?: string;
}

@Component({
  selector: 'app-clientes',
  standalone: false,
  templateUrl: './clientes.component.html',
  styleUrls: ['./clientes.component.scss']
})
export class ClientesComponent extends BaseView<ClienteModel> implements OnInit {

  public form!: ClientesFormDefinition;
  public env = new ClientesEnvironmentDefinition();
  public readonly showAllClientsControl = new FormControl<boolean>(this.env.showAllClients, { nonNullable: true });
  public filter: Filtering<ClienteModel> = new Filtering<ClienteModel>();
  public readonly getRequest!: RequestHandler<ClienteModel[]>;
  public readonly getRequestSdi!: RequestHandler<ClienteModel[]>;
  public readonly deleteRequest!: RequestHandler<ClienteModel>;
  public currentClient: ClienteModel | null = StorageManager.getClient();
  public deleteWarningMessage = '';
  public deleteWarning = '';

  public readonly user = StorageManager.getUser();

  public readonly cols: ColInterface[] = [
    { field: 'nombre',                  header: 'Nombre o Razón Social', filter: '', width: '200px' },
    { field: 'apellido_1',              header: 'Apellido' },
    { field: 'direccion',               header: 'Dirección',   filter: '', visible: environment.appName !== 'avafitogest' },
    { field: 'nif',                     header: 'NIF\u00A0/\u00A0CIF', filter: '' },
    { field: 'localidad',               header: 'Localidad',   filter: '' },
    { field: 'email',                   header: 'Email',       filter: '', width: '200px' },
    { field: 'usuario_asignado_nombre', header: 'Técnico',     filter: '', visible: environment.features.showTecnicoAsignado },
    { field: 'observaciones',           header: 'Observ.',     filter: '', visible: environment.features.showClientesObservaciones, width: '40px' },
    { field: 'cooperativa',             header: 'Cooperativa',             visible: environment.features.showClientesCooperativa },
    { field: 'tipo',                    header: 'Tipo',                    visible: environment.appName === 'agrosalvi' }
  ];

  public readonly colsSdi: ColInterface[] = [
    { field: 'nomcli',   header: 'Nombre',    filter: '', width: '160px' },
    { field: 'razon',    header: 'Razón',     filter: '', width: '160px' },
    { field: 'nifcli',   header: 'NIF',       filter: '' },
    { field: 'dircli',   header: 'Dirección', filter: '' },
    { field: 'dtocli',   header: 'Dto.',      filter: '', width: '60px' },
    { field: 'nomprovi', header: 'Provincia', filter: '' },
    { field: 'pobcli',   header: 'Población', filter: '' },
    { field: 'telcli',   header: 'Teléfono',  filter: '' },
    { field: 'email',    header: 'Email',     filter: '', width: '160px' }
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly dashboard: DashboardService,
    private readonly clientSelectionService: ClientSelectionService,
    public readonly clientesApi: ClientesApiService,
    public readonly sdiApi: SdiApiService,
    private readonly http: HttpClient
  ) {
    super(dashboard, clientesApi.clientesGET, clientesApi.clientesDELETE);
    this.getRequest    = this.clientesApi.clientesGET;
    this.getRequestSdi = this.sdiApi.SdiClientesGET;
    this.deleteRequest = this.clientesApi.clientesDELETE;
  }

  ngOnInit(): void {
    this.dashboard.setMenuItem('clientes');
    this.dashboard.changeClientActive = true;
    this.dashboard.onChangeClient().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.currentClient = this.clientSelectionService.syncFromStorage();
    });
    this.initFilter();
    this.showAllClientsControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.changeShowAll());
  }

  public selectClient(data: ClienteModel): void {
    if (this.clientSelectionService.setSelectedClient(data)) {
      this.currentClient = this.clientSelectionService.getSelectedClient();
    }
  }

  public isCurrentClient(client: ClienteModel): boolean {
    return this.clientSelectionService.sameClient(client, this.currentClient);
  }

  public add(): void { this.router.navigate(['dashboard', 'crear-cliente']); }

  public edit(data: ClienteModel): void {
    this.router.navigate(['dashboard', 'editar-cliente', data.id, data]);
  }

  public override show(modal: string, data: ClienteModel): void {
    super.show(modal, data);
    const formType = modal === 'delete' ? +FormType.DELETION : +FormType.INFORMATIVE;
    this.form = new ClientesFormDefinition(formType, data as Record<string, string>, null);
  }

  public override hide(modal: string): void {
    this.model = {} as ClienteModel;
    this.canShow[modal] = false;
    this.deleteRequest.reset();
  }

  public globalConditionalStyle(_value: string, _col: ColInterface): ColStylesInterface {
    return { textAlign: 'center' };
  }

  public globalVisualTransform(value: string, col: ColInterface, row: TreeNode<ClienteModel>): string {
    if (col.field === 'observaciones') return value ? 'Sí' : '';
    if (col.field === 'nombre') {
      const c = row.data as ClienteModel;
      return c.razon_social?.trim() ? c.razon_social : `${c.nombre} ${c.apellidos ?? ''}`;
    }
    return value;
  }

  public exportarClientes(): void {
    window.location.replace(`${environment.serverUrl}fito_backup/exportar_clientes.php`);
  }

  public downloadCsv(): void {
    this.form = new ClientesFormDefinition(
      +FormType.EXPORT,
      this.filtering.value as unknown as Record<string, string>,
      null
    );
    const csvContent = createCsv(this.form.fieldNames, this.form.fieldLabels, this.filtering.value as ClienteModel[]);

    let csvLink = document.getElementById('csvLink') as HTMLAnchorElement | null;
    if (!csvLink) {
      csvLink = Object.assign(document.createElement('a'), { style: 'display:none' });
      document.body.appendChild(csvLink);
    }
    csvLink.download = 'Clientes.csv';
    csvLink.href = encodeURI(csvContent);
    csvLink.click();
    if (!csvLink.id) csvLink.remove();
  }

  public changeShowAll(): void {
    this.env.showAllClients = this.showAllClientsControl.value;
    sessionStorage.setItem('showAllclientes', String(this.env.showAllClients));
    this.filtering.filter();
  }

  public unsetClientIfSelected(clientId: string): void {
    if (this.clientSelectionService.sameClient(clientId, this.currentClient)) {
      this.clientSelectionService.setSelectedClient(null);
      this.currentClient = null;
    }
  }

  // ── PDF: Adquisición ────────────────────────────────────────────────────────

  public generarAdquisicion(cliente: ClienteModel): void {
    const base = `${environment.serverUrl}ws/index.php?p1=generic`;

    this.http.get<any>(`${base}&p2=stock_new&tipo=E&id_cliente=${cliente.id}`).subscribe({
      next: (resStock) => {
        const stockItems = this.toArray(resStock);
        this.http.get<any>(`${base}&p2=productos&id_cliente=${cliente.id}`).subscribe({
          next: (resProd) => {
            const prodMap = this.toProdMap(this.toArray(resProd));
            this.buildAdquisicionPdf(cliente, this.mapStockToAdquisicionRows(stockItems, prodMap));
          },
          error: (e) => {
            console.error('Error cargando productos para adquisición:', e);
            this.buildAdquisicionPdf(cliente, this.mapStockToAdquisicionRows(stockItems, new Map()));
          }
        });
      },
      error: (e) => {
        console.error('Error cargando stock para adquisición:', e);
        this.buildAdquisicionPdf(cliente, []);
      }
    });
  }

  private mapStockToAdquisicionRows(stockItems: any[], prodMap: Map<number, any>): AdquisicionRow[] {
    const split = this.splitSemicolon;
    return stockItems
      .filter(item => item.tipo_dato === 'Fitosanitario')
      .flatMap(item => {
        const idsProductos = split(item.ids_productos);
        const lotes        = split(item.array_lotes);
        const unidades     = split(item.array_unidades);
        const medidas      = split(item.unidades_medida);
        return unidades.map((unidad, i) => {
          const producto = prodMap.get(+(idsProductos[i] ?? ''));
          return {
            fecha:                      item.fecha             ?? '',
            nombre_comercial:           producto?.nombre       ?? '',
            numero_registro:            producto?.num_registro ?? '',
            numero_lote:                lotes[i]               ?? '',
            clasificacion_toxicologica: '',
            cantidad:                   [unidad, medidas[i] ?? ''].filter(Boolean).join(' '),
            suministrador_nropo:  '',
            suministrador_nif:    'B70913959',
            suministrador_nombre: 'Agrotata Sociedad Limitada',
          };
        });
      });
  }

  private buildAdquisicionPdf(cliente: ClienteModel, rows: AdquisicionRow[]): void {
    const { doc, pageW, marginX, contentW, nombreEmpresa } = this.initPdf(cliente);
    const boxY = 38, boxH = 36;

    this.pdfHeader(doc, pageW, 'ADQUISICIÓN');
    this.pdfEmpresaBox(doc, marginX, contentW, boxY, boxH, [
      ['DENOMINACIÓN DE LA EMPRESA:', nombreEmpresa],
      ['NIF/CIF:',   cliente.nif       ?? ''],
      ['Dirección:', cliente.direccion ?? ''],
    ], [
      ['Localidad:', cliente.localidad     ?? ''],
      ['PROVINCIA:', cliente.provincia     ?? ''],
      ['C.P.:',      cliente.codigo_postal ?? ''],
      ['Teléfono:',  cliente.telefono      ?? ''],
      ['Nº ROPO:',   cliente.ropo          ?? ''],
    ]);

    const head = [
      [
        { content: 'FECHA DE LA\nOPERACIÓN',                   rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
        { content: 'IDENTIFICACIÓN DEL PRODUCTO FITOSANITARIO', colSpan: 4, styles: { halign: 'center' } },
        { content: 'CANTIDAD\n(KG o L.)',                       rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
        { content: 'IDENTIFICACIÓN DEL SUMINISTRADOR',          colSpan: 3, styles: { halign: 'center' } },
      ],
      [
        { content: 'NOMBRE\nCOMERCIAL',           styles: { halign: 'center' } },
        { content: 'NÚMERO DE\nREGISTRO',          styles: { halign: 'center' } },
        { content: 'NÚMERO DE\nLOTE',              styles: { halign: 'center' } },
        { content: 'CLASIFICACIÓN\nTOXICOLÓGICA', styles: { halign: 'center' } },
        { content: 'NºROPO',                       styles: { halign: 'center' } },
        { content: 'NIF',                          styles: { halign: 'center' } },
        { content: 'Nombre',                       styles: { halign: 'center' } },
      ],
    ];

    const bodyRows = rows.length
      ? rows.map(r => [r.fecha ?? '', r.nombre_comercial ?? '', r.numero_registro ?? '', r.numero_lote ?? '', r.clasificacion_toxicologica ?? '', r.cantidad ?? '', r.suministrador_nropo ?? '', r.suministrador_nif ?? '', r.suministrador_nombre ?? ''])
      : Array(8).fill(Array(9).fill(''));

    this.pdfAutoTable(doc, boxY + boxH + 4, head, bodyRows, marginX, {
      0: { cellWidth: 22 }, 1: { cellWidth: 38 }, 2: { cellWidth: 26 },
      3: { cellWidth: 22 }, 4: { cellWidth: 30 }, 5: { cellWidth: 20 },
      6: { cellWidth: 22 }, 7: { cellWidth: 26 }, 8: { cellWidth: 'auto' as any },
    });

    this.savePdf(doc, 'Adquisicion', nombreEmpresa);
  }

  // ── PDF: Aplicación ─────────────────────────────────────────────────────────

  public generarAplicacion(cliente: ClienteModel): void {
    const base = `${environment.serverUrl}ws/index.php?p1=generic`;
    const id   = cliente.id;

    this.http.get<any>(`${base}&p2=tareas_fitosanitarias&id_cliente=${id}`).subscribe({
      next: (resTareas) => {
        const tareas = this.toArray(resTareas);
        if (!tareas.length) { this.buildAplicacionPdf(cliente, []); return; }

        Promise.all([
          this.http.get<any>(`${base}&p2=tratamientos&id_cliente=${id}`).toPromise(),
          this.http.get<any>(`${base}&p2=parcelas&id_cliente=${id}`).toPromise(),
          this.http.get<any>(`${base}&p2=productos&id_cliente=${id}`).toPromise(),
        ]).then(([resTrat, resParcelas, resProd]) => {
          const tratMap = new Map<number, any>(this.toArray(resTrat).map((t: any)     => [+t.id, t]));
          const parcMap = new Map<number, any>(this.toArray(resParcelas).map((p: any) => [+p.id, p]));
          const prodMap = this.toProdMap(this.toArray(resProd));
          this.buildAplicacionPdf(cliente, this.mapTareasToAplicacionRows(tareas, tratMap, parcMap, prodMap));
        }).catch(e => {
          console.error('Error cargando datos para aplicación:', e);
          this.buildAplicacionPdf(cliente, []);
        });
      },
      error: (e) => {
        console.error('Error cargando tareas para aplicación:', e);
        this.buildAplicacionPdf(cliente, []);
      }
    });
  }

  private mapTareasToAplicacionRows(
    tareas: any[],
    tratMap: Map<number, any>,
    parcMap: Map<number, any>,
    prodMap: Map<number, any>
  ): AplicacionRow[] {
    const split = this.splitSemicolon;
    return tareas.flatMap(tarea => {
      const tratamiento  = tratMap.get(+tarea.id_tratamiento);
      const idsProductos = split(tratamiento?.ids_productos);
      const cantidades   = split(tratamiento?.cantidades_real);
      const primerParcela = parcMap.get(+(split(tarea.ids_parcelas)[0] ?? ''));
      const sigpacProvincia = primerParcela?.cod_provincia ?? '';
      const sigpacMunicipio = primerParcela?.municipio     ?? '';
      const sigpacParcRec   = primerParcela
        ? [primerParcela.poligono, primerParcela.numero, primerParcela.recinto].filter(Boolean).join('-')
        : '';
      const sigpac = { sigpac_provincia: sigpacProvincia, sigpac_municipio: sigpacMunicipio, sigpac_parc_rec: sigpacParcRec };
      const contratante = {
        contratante_razon: 'Agrotata Sociedad Limitada',
        contratante_sede:  'Av. De La Ribera Alta, 7, Alberic, 46260, Valencia',
        contratante_nif:   'B70913959',
      };

      if (!idsProductos.length) {
        return [{ fecha: tarea.fecha_inicio ?? '', nombre_comercial: '', numero_registro: '', numero_lote: tarea.lote ?? '', capacidad_envase: '', numero_envases: '', ...sigpac, ...contratante }];
      }

      return idsProductos.map((idProd, i) => {
        const producto = prodMap.get(+idProd);
        return {
          fecha:            tarea.fecha_inicio     ?? '',
          nombre_comercial: producto?.nombre       ?? '',
          numero_registro:  producto?.num_registro ?? '',
          numero_lote:      tarea.lote             ?? '',
          capacidad_envase: cantidades[i]          ?? '',
          numero_envases:   producto?.envase       ?? '',
          ...sigpac,
          ...contratante,
        };
      });
    });
  }

  private buildAplicacionPdf(cliente: ClienteModel, rows: AplicacionRow[]): void {
    const { doc, pageW, marginX, contentW, nombreEmpresa } = this.initPdf(cliente);
    const boxY = 38, boxH = 28;

    this.pdfHeader(doc, pageW, 'APLICACIÓN');
    this.pdfEmpresaBox(doc, marginX, contentW, boxY, boxH, [
      ['DENOMINACIÓN DE LA EMPRESA:', nombreEmpresa],
      ['Dirección:', cliente.direccion ?? ''],
      ['Localidad:', cliente.localidad ?? ''],
      ['Teléfono:',  cliente.telefono  ?? ''],
      ['NºROPO:',    cliente.ropo      ?? ''],
    ], [
      ['C.P.:',      cliente.codigo_postal ?? ''],
      ['PROVINCIA:', cliente.provincia     ?? ''],
    ], 11);

    const head = [
      [
        { content: 'FECHA DE LA\nOPERACIÓN',                   rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
        { content: 'IDENTIFICACIÓN DEL PRODUCTO FITOSANITARIO', colSpan: 3, styles: { halign: 'center' } },
        { content: 'CANTIDAD DE PRODUCTO',                      colSpan: 2, styles: { halign: 'center' } },
        { content: 'IDENTIFICACIÓN SIGPAC',                     colSpan: 3, styles: { halign: 'center' } },
        { content: 'PARTE CONTRATANTE DEL SERVICIO',            colSpan: 3, styles: { halign: 'center' } },
      ],
      [
        { content: 'NOMBRE\nCOMERCIAL',               styles: { halign: 'center' } },
        { content: 'NÚMERO DE\nREGISTRO',              styles: { halign: 'center' } },
        { content: 'NÚMERO DE\nLOTE',                  styles: { halign: 'center' } },
        { content: 'Capacidad del\nenvase\n(Kg o L)',  styles: { halign: 'center' } },
        { content: 'Número de\nenvases',               styles: { halign: 'center' } },
        { content: 'Provincia',                        styles: { halign: 'center' } },
        { content: 'Mun',                              styles: { halign: 'center' } },
        { content: 'Parc-Rec',                         styles: { halign: 'center' } },
        { content: 'Razón Social',                     styles: { halign: 'center' } },
        { content: 'Sede',                             styles: { halign: 'center' } },
        { content: 'Nif',                              styles: { halign: 'center' } },
      ],
    ];

    const bodyRows = rows.length
      ? rows.map(r => [r.fecha ?? '', r.nombre_comercial ?? '', r.numero_registro ?? '', r.numero_lote ?? '', r.capacidad_envase ?? '', r.numero_envases ?? '', r.sigpac_provincia ?? '', r.sigpac_municipio ?? '', r.sigpac_parc_rec ?? '', r.contratante_razon ?? '', r.contratante_sede ?? '', r.contratante_nif ?? ''])
      : Array(8).fill(Array(12).fill(''));

    this.pdfAutoTable(doc, boxY + boxH + 4, head, bodyRows, marginX, {
      0:  { cellWidth: 18 }, 1:  { cellWidth: 30 }, 2:  { cellWidth: 22 },
      3:  { cellWidth: 18 }, 4:  { cellWidth: 20 }, 5:  { cellWidth: 16 },
      6:  { cellWidth: 14 }, 7:  { cellWidth: 16 }, 8:  { cellWidth: 14 },
      9:  { cellWidth: 38 }, 10: { cellWidth: 34 }, 11: { cellWidth: 'auto' as any },
    });

    this.savePdf(doc, 'Aplicacion', nombreEmpresa);
  }

  // ── Helpers PDF ─────────────────────────────────────────────────────────────

  private initPdf(cliente: ClienteModel): { doc: jsPDF; pageW: number; marginX: number; contentW: number; nombreEmpresa: string } {
    const doc          = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW        = doc.internal.pageSize.getWidth();
    const marginX      = 14;
    const contentW     = pageW - marginX * 2;
    const nombreEmpresa = cliente.razon_social?.trim()
      ? cliente.razon_social
      : `${cliente.nombre} ${cliente.apellidos ?? ''}`.trim();
    return { doc, pageW, marginX, contentW, nombreEmpresa };
  }

  private pdfHeader(doc: jsPDF, pageW: number, tipo: string): void {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('REGISTRO DE TRANSACCIONES CON PRODUCTOS FITOSANITARIOS', pageW / 2, 18, { align: 'center' });
    doc.setFontSize(9);
    doc.text('(SECTOR TRATAMIENTOS)', pageW / 2, 24, { align: 'center' });
    doc.text('Artículo 25 Real Decreto 1311/2012 modificado por el ANEXO I del Real Decreto 285/2021', pageW / 2, 29, { align: 'center' });
    doc.text(tipo, pageW / 2, 34, { align: 'center' });
  }

  private pdfEmpresaBox(
    doc: jsPDF,
    marginX: number,
    contentW: number,
    boxY: number,
    boxH: number,
    colIzq: [string, string][],
    colDer: [string, string][],
    col2OffsetY = 6
  ): void {
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(marginX, boxY, contentW, boxH);

    const lx = marginX + 3, lineH = 5;
    const labelValue = (label: string, value: string, x: number, y: number) => {
      doc.setFont('helvetica', 'bold');   doc.text(label, x, y);
      doc.setFont('helvetica', 'normal'); doc.text(value, x + doc.getTextWidth(label) + 2, y);
    };

    let ly = boxY + 6;
    for (const [label, value] of colIzq) { labelValue(label, value, lx, ly); ly += lineH; }

    const col2x = marginX + contentW / 2;
    let col2y = boxY + col2OffsetY;
    for (const [label, value] of colDer) { labelValue(label, value, col2x, col2y); col2y += lineH; }
  }

  private pdfAutoTable(doc: jsPDF, startY: number, head: any[], body: string[][], marginX: number, columnStyles: Record<number, object>): void {
    autoTable(doc, {
      startY,
      head,
      body,
      theme: 'grid',
      styles:     { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.2, textColor: [0, 0, 0] },
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 6.5, lineWidth: 0.3 },
      columnStyles,
      margin: { left: marginX, right: marginX },
    });
  }

  private savePdf(doc: jsPDF, prefix: string, nombreEmpresa: string): void {
    const nombre = nombreEmpresa.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_\-]/g, '');
    doc.save(`${prefix}_${nombre}.pdf`);
  }

  // ── Helpers datos ───────────────────────────────────────────────────────────

  private toArray(res: any): any[] {
    return Array.isArray(res) ? res : (res?.data ?? []);
  }

  private toProdMap(productos: any[]): Map<number, any> {
    return new Map<number, any>(productos.map(p => [+p.id, p]));
  }

  private readonly splitSemicolon = (val: string | null): string[] =>
    (val ?? '').split(';').map(v => v.trim()).filter(Boolean);

  // ── Filtros ─────────────────────────────────────────────────────────────────

  private initFilter(): void {
    const { appName } = environment;

    if (appName === 'benibal' || appName === 'avafitogest') {
      this.filtering.addFilter((data: TreeNode<ClienteModel>[] | void) =>
        (data ?? []).filter(it => it).filter(it =>
          appName === 'avafitogest' ? true : (!this.env.showAllClients ? it.data?.acceso_plataforma === 1 : true)
        )
      );
    } else if (appName === 'agrosalvi') {
      if (this.user.tipo !== 'Agro + Garden') {
        this.filtering.addFilter((data: TreeNode<ClienteModel>[] | void) =>
          (data ?? []).filter(it => it).filter(it => !this.env.showAllClients ? this.user.tipo === it.data?.tipo : true)
        );
      }
    } else if (appName === 'innovia' && this.user.rol === 'user') {
      this.filtering.addFilter((data: TreeNode<ClienteModel>[] | void) =>
        (data ?? []).filter(it => (it as any).id.toString() === this.user.id_cliente?.toString())
      );
    } else {
      this.filtering.addFilter((data: TreeNode<ClienteModel>[] | void) =>
        (data ?? []).filter(it => it).filter(it =>
          !this.env.showAllClients ? [it.data?.id_usuario_asignado, it.data?.id_usuario_asignado2].includes(this.user.id) : true
        )
      );
    }
  }
}